import Input from 'components/atoms/Input'
import { Form } from 'formik'
import { get, upperFirst, forEach, find, isObject, isArray } from 'lodash'
import OtpTimer from 'components/atoms/OtpTimer'
import Selection from 'components/molecules/Selection'
import { useState, useEffect } from 'react'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import apiGet from 'lib/network/apiGet'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

const LabelContent = ({ label, required }) => (
  <span className="inline-flex items-center gap-1">
    <span>{label}</span>
    {required && <span className="text-red-500">*</span>}
  </span>
)

const FieldLabel = ({ htmlFor, label, required }) => (
  <label htmlFor={htmlFor} className="block text-sm font-normal text-gray-700 mb-1">
    <LabelContent label={label} required={required} />
  </label>
)

const CarAuth = ({ onSuccess, close }) => {
  const [status, setStatus] = useState('')
  const [marketSegment, setMarketSegment] = useState('')
  const [shifter, setShifter] = useState('')
  const [engineSize, setEngineSize] = useState('')
  const [plateCategory, setPlateCategory] = useState('')
  const [insurance, setInsurance] = useState('')
  const [fuelPolicy, setFuelPolicy] = useState('')
  const [companyNames, setCompanyNames] = useState([])
  const [selectedCompany, setSelectedCompany] = useState('')
  const [modelYear, setModelYear] = useState('')
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'

  const getLocalizedLabel = (enValue, arValue) => {
    if (isArabic) {
      return arValue || enValue || ''
    }
    return enValue || arValue || ''
  }

  const createOption = (id, enValue, arValue) => ({
    id,
    enName: enValue || '',
    arName: arValue || '',
    name: getLocalizedLabel(enValue || '', arValue || ''),
  })

  const relocalizeOptions = options =>
    options?.map(option => {
      if (!option) return option
      const { enName, arName, name, ...rest } = option
      const baseEn = enName ?? name
      const baseAr = arName ?? name
      return {
        ...rest,
        id: option.id,
        enName: baseEn,
        arName: baseAr,
        name: getLocalizedLabel(baseEn, baseAr),
      }
    }) || []

  const [branches, setBranches] = useState([])
  const [selectedBranch, setSelectedBranch] = useState('')
  const [makes, setMakes] = useState([])
  const [selectedMake, setSelectedMake] = useState('')
  const [models, setModels] = useState([])
  const [selectedModel, setSelectedModel] = useState('')
  const [colors, setColors] = useState([])
  const [selectedColor, setSelectedColor] = useState('')

  const [selectedMakes, setSelectedMakes] = useState([])
  const [selectedModels, setSelectedModels] = useState([])
  const [selectedColors, setSelectedColors] = useState([])
  const [insurances, setInsurances] = useState([])
  const [fuelPolicies, setFuelPolicies] = useState([])

  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  const isSuperCompanyUser = userFromStore?.superCompanyUser
  const userCompanyId = userFromStore?.company?._id
  // A plain company-dashboard user (non-admin, non-super) may be linked to multiple branches
  // and must pick which one the car belongs to.
  const isPlainCompanyUser = isCompanyRole && !hasAdminAccess && !isSuperCompanyUser

  const [formData, setFormData] = useState({
    registrationNumber: '',
    plateCategory: '',
    depositAmount: '',
    dailyRate: '',
    yearlyRate: '',
    weeklyRate: '',
    monthlyRate: '',
    allowedKMPerDay: '',
    extraKMPerDay: '',
    extraKMPerWeek: '',
    extraKMPerMonth: '',
    extraKMPerYear: '',
    description: '',
    insuranceFee: '',
    trim: '',
    numberOfDoors: '',
    numberOfSeats: '',
    engineSize: '',
  })

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleAddCar = async () => {
    const needsCompanySelection = hasAdminAccess
    const needsBranchSelection = hasAdminAccess || isSuperCompanyUser || isPlainCompanyUser

    if (needsCompanySelection && !selectedCompany) {
      notify.error(t('carAuth.selectCompanyError'))
      return
    }

    if (needsBranchSelection && !selectedBranch) {
      notify.error(t('carAuth.selectBranchError'))
      return
    }
    // eslint-disable-next-line
    if (!formData.registrationNumber || !formData.registrationNumber.trim()) {
      notify.error(t('carAuth.registrationNumberRequired'))
      return
    }

    if (!plateCategory) {
      notify.error(t('carAuth.plateCategoryRequired'))
      return
    }

    if (!selectedModel) {
      notify.error(t('carAuth.selectModelError'))
      return
    }

    const selectedModelOption = find(models, { id: selectedModel }) || selectedModels[0]
    if (!selectedModelOption?.marketSegment) {
      notify.error(t('carAuth.messages.modelMissingMarketSegment'))
      return
    }

    if (formData.yearlyRate === '' || formData.yearlyRate === null || formData.yearlyRate === undefined) {
      notify.error(t('carAuth.yearlyRateRequired'))
      return
    }

    if (formData.numberOfDoors === '' || formData.numberOfDoors === null || formData.numberOfDoors === undefined) {
      notify.error(t('carAuth.numberOfDoorsRequired'))
      return
    }

    if (formData.numberOfSeats === '' || formData.numberOfSeats === null || formData.numberOfSeats === undefined) {
      notify.error(t('carAuth.numberOfSeatsRequired'))
      return
    }

    if (!engineSize) {
      notify.error(t('carAuth.engineSizeRequired'))
      return
    }

    const payload = {
      ...formData,
      // eslint-disable-next-line
      registrationNumber: formData.registrationNumber.trim(),
      yearlyRate: Number(formData.yearlyRate),
      numberOfDoors: Number(formData.numberOfDoors),
      numberOfSeats: Number(formData.numberOfSeats),
      fuelPolicy,
      insurance,
      status,
      modelYear,
      marketSegment,
      shifter,
      engineSize: Number(engineSize),
      plateCategory,
      make: selectedMake,
      model: selectedModel,
      color: selectedColor,
      company: isCompanyRole && !hasAdminAccess ? userCompanyId : selectedCompany,
      ...(selectedBranch ? { branch: selectedBranch } : {}),
    }

    const response = await apiPost('cars/add', {
      ...payload,
    })
    if (response.success) {
      notify.success(t('carAuth.messages.carAddedSuccessfully'))
      onSuccess(response.data._id)
    }
  }

  const fetchColors = async (searchQuery = '') => {
    try {
      const response = await apiGet('colors', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const colorsList = response.data.colors.map(color => createOption(color._id, color.color, color.colorAr))
        const combinedColors = [...colorsList]
        forEach(selectedColors, selectedColor => {
          if (!find(combinedColors, { id: selectedColor.id })) {
            combinedColors.push(selectedColor)
          }
        })
        setColors(combinedColors)
      } else {
        setColors(selectedColors)
      }
    } catch (error) {
      notify.error(t('carAuth.messages.failedToFetchColors'))
      setColors(selectedColors)
    }
  }

  useEffect(() => {
    if (hasAdminAccess) {
      fetchCompanies()
    }
    fetchMakes()
    fetchModels()
    fetchColors()
  }, [hasAdminAccess])

  const fetchCompanies = async () => {
    const response = await apiGet('companies', { pageSize: 1000 })

    if (response?.data) {
      const companies = response.data.company.map(company => createOption(company._id, company.name, company.nameAr))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
  }

  const fetchMakes = async (searchQuery = '') => {
    try {
      const response = await apiGet('makes', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const makesList = response.data.makes.map(make => createOption(make._id, make.make, make.makeAr))
        const combinedMakes = [...makesList]
        forEach(selectedMakes, selectedMake => {
          if (!find(combinedMakes, { id: selectedMake.id })) {
            combinedMakes.push(selectedMake)
          }
        })
        setMakes(combinedMakes)
      } else {
        setMakes(selectedMakes)
      }
    } catch (error) {
      notify.error(t('carAuth.messages.failedToFetchMakes'))
      setMakes(selectedMakes)
    }
  }

  const fetchModels = async (searchQuery = '') => {
    try {
      const response = await apiGet('modals', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const modelsList = response.data.modals.map(model => {
          const segment = model.marketSegment
          const segmentName = isObject(segment) ? segment?.marketSegment : ''
          const segmentAr = isObject(segment) ? segment?.marketSegmentAr : ''
          return {
            ...createOption(model._id, model.modal, model.modalAr),
            marketSegment: segmentName || '',
            marketSegmentAr: segmentAr || '',
          }
        })
        const combinedModels = [...modelsList]
        forEach(selectedModels, selectedModel => {
          if (!find(combinedModels, { id: selectedModel.id })) {
            combinedModels.push(selectedModel)
          }
        })
        setModels(combinedModels)
      } else {
        setModels(selectedModels)
      }
    } catch (error) {
      notify.error(t('carAuth.messages.failedToFetchModels'))
      setModels(selectedModels)
    }
  }

  useEffect(() => {
    if (isSuperCompanyUser && userCompanyId) {
      fetchBranches(userCompanyId)
    }
  }, [isSuperCompanyUser, userCompanyId])

  // Populate the branch selector for a plain company user from their own assigned branches.
  useEffect(() => {
    if (!isPlainCompanyUser) return
    // eslint-disable-next-line
    const assigned = isArray(userFromStore?.branch) ? userFromStore.branch : []
    const branchList = assigned.filter(b => b && b._id).map(b => createOption(b._id, b.name, b.nameAr))
    setBranches(branchList)
    if (branchList.length === 1) {
      setSelectedBranch(branchList[0].id)
    }
    // eslint-disable-next-line
  }, [isPlainCompanyUser, userFromStore?.branch])
  const fetchBranches = async companyId => {
    if (!companyId) {
      setBranches([])
      return
    }

    try {
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.data) {
        const branchList = response.data.map(branch => createOption(branch._id, branch.name, branch.nameAr))
        setBranches(branchList)
      } else {
        setBranches([])
      }
    } catch (error) {
      notify.error(t('carAuth.messages.failedToFetchBranches'))
      setBranches([])
    }
  }
  const fetchInsurances = async (searchQuery = '') => {
    try {
      const response = await apiGet('insurances', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const insuranceList = response.data.insurance.map(item => createOption(item.insurance, item.insurance, item.insuranceAr))
        setInsurances(insuranceList)
      } else {
        setInsurances([])
      }
    } catch (error) {
      notify.error(t('carAuth.messages.failedToFetchInsurances'))
      setInsurances([])
    }
  }
  const fetchFuelPolicy = async (searchQuery = '') => {
    try {
      const response = await apiGet('fuel-policies', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const fuelPolicyList = response.data.fuelPolicy.map(item => createOption(item.fuelPolicy, item.fuelPolicy, item.fuelPolicyAr))
        setFuelPolicies(fuelPolicyList)
      } else {
        setFuelPolicies([])
      }
    } catch (error) {
      notify.error(t('carAuth.messages.failedToFetchFuelPolicies'))
      setFuelPolicies([])
    }
  }

  useEffect(() => {
    fetchInsurances()
    fetchFuelPolicy()
  }, [])

  useEffect(() => {
    setMakes(prev => relocalizeOptions(prev))
    setModels(prev => relocalizeOptions(prev))
    setColors(prev => relocalizeOptions(prev))
    setInsurances(prev => relocalizeOptions(prev))
    setFuelPolicies(prev => relocalizeOptions(prev))
    setCompanyNames(prev => relocalizeOptions(prev))
    setBranches(prev => relocalizeOptions(prev))
    setSelectedMakes(prev => relocalizeOptions(prev))
    setSelectedModels(prev => relocalizeOptions(prev))
    setSelectedColors(prev => relocalizeOptions(prev))
  }, [i18n.language])

  const currentYear = new Date().getFullYear()
  const startYear = 1995

  const years = Array.from({ length: currentYear - startYear + 1 }, (v, k) => {
    const year = startYear + k
    return { id: year, name: year }
  })

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div>
          <FieldLabel htmlFor="modelYear" label={t('carAuth.modalYear')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setModelYear(prevValue => (prevValue === value.id ? '' : value.id))
              }
            }}
            title={t('carAuth.selectModalYear')}
            list={years}
            value={modelYear}
            className="w-full"
          />
        </div>
        <div>
          <FieldLabel htmlFor="registrationNumber" label={t('carProfile.form.registrationNumber')} required />
          <input
            type="text"
            name="registrationNumber"
            id="registrationNumber"
            value={formData.registrationNumber}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="plateCategory" label={t('carAuth.plateCategory')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setPlateCategory(prevValue => (prevValue === value.id ? '' : value.id))
              }
            }}
            title={t('carAuth.selectPlateCategory')}
            list={[
              { id: 'private', name: t('carProfile.plateCategoryOptions.private') },
              { id: 'commercial', name: t('carProfile.plateCategoryOptions.commercial') },
            ]}
            value={plateCategory}
            className="w-full"
          />
        </div>
        <div>
          <FieldLabel htmlFor="depositAmount" label={t('carAuth.depositAmount')} required />
          <input
            type="number"
            name="depositAmount"
            id="depositAmount"
            value={formData.depositAmount}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="dailyRate" label={t('carAuth.dailyRate')} required />
          <input
            type="number"
            name="dailyRate"
            id="dailyRate"
            value={formData.dailyRate}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="yearlyRate" label={t('carAuth.yearlyRate')} required />
          <input
            type="number"
            name="yearlyRate"
            id="yearlyRate"
            value={formData.yearlyRate}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="weeklyRate" label={t('carAuth.weeklyRate')} required />
          <input
            type="number"
            name="weeklyRate"
            id="weeklyRate"
            value={formData.weeklyRate}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="monthlyRate" label={t('carAuth.monthlyRate')} required />
          <input
            type="number"
            name="monthlyRate"
            id="monthlyRate"
            value={formData.monthlyRate}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {/* Delivery is priced from the branch's km ranges (Branch > Delivery tab),
            so there is no per-car delivery cost to enter here any more. */}
        <div>
          <FieldLabel htmlFor="allowedKMPerDay" label={t('carAuth.allowedKMPerDay')} required />
          <input
            type="number"
            name="allowedKMPerDay"
            id="allowedKMPerDay"
            value={formData.allowedKMPerDay}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="extraKMPerDay" label={t('carProfile.form.extraKMPerDay')} required />
          <input
            type="number"
            name="extraKMPerDay"
            id="extraKMPerDay"
            value={formData.extraKMPerDay}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="extraKMPerWeek" label={t('carProfile.form.extraKMPerWeek')} required />
          <input
            type="number"
            name="extraKMPerWeek"
            id="extraKMPerWeek"
            value={formData.extraKMPerWeek}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="extraKMPerMonth" label={t('carProfile.form.extraKMPerMonth')} required />
          <input
            type="number"
            name="extraKMPerMonth"
            id="extraKMPerMonth"
            value={formData.extraKMPerMonth}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="extraKMPerYear" label={t('carProfile.form.extraKMPerYear')} required />
          <input
            type="number"
            name="extraKMPerYear"
            id="extraKMPerYear"
            value={formData.extraKMPerYear}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="fuelPolicy" label={t('carAuth.fuelPolicy')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setFuelPolicy(prevValue => (prevValue === value.id ? '' : value.id))
              }
            }}
            title={t('carAuth.selectFuel')}
            list={fuelPolicies}
            showSearch
            onSearch={searchQuery => fetchFuelPolicy(searchQuery)}
            value={fuelPolicy}
            className="w-full"
          />
        </div>
        <div>
          <FieldLabel htmlFor="insurance" label={t('carAuth.insurance')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setInsurance(prevValue => (prevValue === value.id ? '' : value.id))
              }
            }}
            title={t('carAuth.selectInsurance')}
            // list={[
            //   { id: '3rd Party Insurance', name: '3rd Party Insurance' },
            //   { id: 'Full Insurance', name: 'Full Insurance' },
            // ]}
            list={insurances}
            showSearch
            onSearch={searchQuery => fetchInsurances(searchQuery)}
            value={insurance}
            className="w-full"
          />
        </div>
        <div>
          <FieldLabel htmlFor="insuranceFee" label={t('carAuth.insuranceFee')} required />
          <input
            type="number"
            name="insuranceFee"
            id="insuranceFee"
            value={formData.insuranceFee}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="color" label={t('carAuth.color')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setSelectedColor(prevValue => (prevValue === value.id ? '' : value.id))
                const newSelectedColor = {
                  id: value.id,
                  enName: value.enName || value.name,
                  arName: value.arName || value.name,
                  name: value.name,
                }
                setSelectedColors([newSelectedColor])
                if (!find(colors, { id: value.id })) {
                  setColors(prev => [...prev, newSelectedColor])
                }
              }
            }}
            title={t('carAuth.selectColor')}
            list={colors}
            value={selectedColor}
            showSearch
            onSearch={searchQuery => fetchColors(searchQuery)}
            className="w-full"
          />
        </div>

        <div>
          <FieldLabel htmlFor="status" label={t('carAuth.status')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setStatus(prevValue => (prevValue === value.id ? '' : value.id))
              }
            }}
            title={t('carAuth.selectStatus')}
            list={[
              { id: 'active', name: getLocalizedLabel('Active', t('carProfile.statusOptions.active')) },
              { id: 'booked', name: getLocalizedLabel('Booked', t('cars.status.booked')) },
              { id: 'out of service', name: getLocalizedLabel('Out of Service', t('carProfile.statusOptions.outOfService')) },
              { id: 'inProgress', name: getLocalizedLabel('In Progress', t('carProfile.statusOptions.inProgress')) },
            ]}
            value={status}
            className="w-full"
          />
        </div>
        {hasAdminAccess && (
          <div>
            <FieldLabel htmlFor="company" label={t('carAuth.company')} required />
            <Selection
              onChange={value => {
                if (value && value.id) {
                  setSelectedCompany(prevValue => {
                    const newValue = prevValue === value.id ? '' : value.id
                    fetchBranches(newValue)
                    return newValue
                  })
                  setSelectedBranch('')
                }
              }}
              title={t('carAuth.selectCompany')}
              list={companyNames}
              value={selectedCompany}
              className="w-full"
            />
          </div>
        )}
        {(hasAdminAccess || isSuperCompanyUser || isPlainCompanyUser) && (
          <div>
            <FieldLabel htmlFor="branch" label={t('carAuth.branch')} required />
            <Selection
              onChange={value => {
                if (value && value.id) {
                  setSelectedBranch(prevValue => (prevValue === value.id ? '' : value.id))
                }
              }}
              title={t('carAuth.selectBranch')}
              list={branches}
              value={selectedBranch}
              className="w-full"
            />
          </div>
        )}

        <div>
          <FieldLabel htmlFor="make" label={t('carAuth.make')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setSelectedMake(prevValue => (prevValue === value.id ? '' : value.id))
                const newSelectedMake = {
                  id: value.id,
                  enName: value.enName || value.name,
                  arName: value.arName || value.name,
                  name: value.name,
                }
                setSelectedMakes([newSelectedMake])
                if (!find(makes, { id: value.id })) {
                  setMakes(prev => [...prev, newSelectedMake])
                }
              }
            }}
            title={t('carAuth.selectMake')}
            list={makes}
            value={selectedMake}
            showSearch
            onSearch={searchQuery => fetchMakes(searchQuery)}
            className="w-full"
          />
        </div>
        <div>
          <FieldLabel htmlFor="model" label={t('carAuth.model')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                const isDeselecting = selectedModel === value.id
                setSelectedModel(prevValue => (prevValue === value.id ? '' : value.id))
                if (isDeselecting) {
                  setSelectedModels([])
                  setMarketSegment('')
                  return
                }
                const newSelectedModel = {
                  id: value.id,
                  enName: value.enName || value.name,
                  arName: value.arName || value.name,
                  name: value.name,
                  marketSegment: value.marketSegment || '',
                  marketSegmentAr: value.marketSegmentAr || '',
                }
                setSelectedModels([newSelectedModel])
                if (!find(models, { id: value.id })) {
                  setModels(prev => [...prev, newSelectedModel])
                }
                setMarketSegment(value.marketSegment || '')
              }
            }}
            title={t('carAuth.selectModel')}
            list={models}
            value={selectedModel}
            showSearch
            onSearch={searchQuery => fetchModels(searchQuery)}
            className="w-full"
          />
        </div>

        <div>
          <FieldLabel htmlFor="shifter" label={t('carAuth.shifter')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setShifter(prevValue => (prevValue === value.id ? '' : value.id))
              }
            }}
            title={t('carAuth.selectShifter')}
            list={[
              { id: 'Manual', name: getLocalizedLabel('Manual', t('carProfile.shifterOptions.manual')) },
              { id: 'Automatic', name: getLocalizedLabel('Automatic', t('carProfile.shifterOptions.automatic')) },
            ]}
            value={shifter}
            className="w-full"
          />
        </div>
        {/* <div>
          <label htmlFor="trim" className="block text-sm font-normal text-gray-700 mb-1">
            Trim
          </label>
          <input type="text" name="trim" id="trim" value={formData.trim} onChange={handleChange} className="w-full" />
        </div> */}
        <div>
          <FieldLabel htmlFor="numberOfDoors" label={t('carAuth.numberOfDoors')} required />
          <input
            type="number"
            name="numberOfDoors"
            id="numberOfDoors"
            value={formData.numberOfDoors}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <FieldLabel htmlFor="numberOfSeats" label={t('carAuth.numberOfSeats')} required />
          <input
            type="number"
            name="numberOfSeats"
            id="numberOfSeats"
            value={formData.numberOfSeats}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <FieldLabel htmlFor="engineSize" label={t('carAuth.engineSize')} required />
          <Selection
            onChange={value => {
              if (value && value.id) {
                setEngineSize(prevValue => (prevValue === value.id ? '' : value.id))
              }
            }}
            title={t('carAuth.selectEngine')}
            list={[
              { id: 3, name: '3' },
              { id: 4, name: '4' },
              { id: 5, name: '5' },
              { id: 6, name: '6' },
              { id: 8, name: '8' },
            ]}
            value={engineSize}
            className="w-full"
          />
        </div>
      </div>
      {/* <div>
        <label htmlFor="description" className="block text-sm font-normal text-gray-700 mb-1">
          Description
        </label>
        <textarea type="text" name="description" id="description" rows="5" value={formData.description} onChange={handleChange} className="w-full" />
      </div> */}

      <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
        <button
          type="button"
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
          onClick={handleAddCar}
        >
          {t('carAuth.addCar')}
        </button>
        <button
          type="button"
          className="w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none"
          onClick={() => close()}
        >
          {t('carAuth.close')}
        </button>
      </div>
    </div>
  )
}

export default CarAuth
